import { ReactNode } from 'react';
import classNames from 'classnames';
import { Dialog } from '@headlessui/react';
import { Check, X } from 'lucide-react';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import { priceFormatSuperscript } from '@/lib/price';
import { getPlanMonthlyPrice, getPlanMonths } from '@/lib/reebelocare/plan';
import { priceFormater } from '@/settings';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

// Tints the ReebeloCare column, header to last row.
const PLAN_COLUMN = 'bg-[#F0FFF5]';

const Covered = ({ covered }: { covered: boolean }) =>
  covered ? (
    <span className="inline-flex items-center gap-1 font-medium text-[#128452]">
      <Check className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
      {t`Yes`}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-gray-700/60">
      <X className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
      {t`No`}
    </span>
  );

const Row = ({
  label,
  hint,
  plan,
  standard,
}: {
  label: string;
  hint?: string;
  plan: ReactNode;
  standard: ReactNode;
}) => (
  <tr className="border-t border-[#F2F2F2]">
    <th scope="row" className="py-2.5 pr-2 text-left font-normal sm:pr-3">
      <span className="block">{label}</span>
      {hint && (
        <span className="mt-0.5 block text-xs text-gray-700/60">{hint}</span>
      )}
    </th>
    <td className={classNames('px-2 py-2.5 text-center sm:px-3', PLAN_COLUMN)}>
      {plan}
    </td>
    <td className="px-2 py-2.5 text-center sm:px-3">{standard}</td>
  </tr>
);

/**
 * "Compare coverage" in the cart: ReebeloCare against the warranty the device
 * comes with, and the choice between the two.
 */
const CompareCoverageModal = ({
  open,
  onClose,
  productTitle,
  plan,
  standardWarranty,
  isPlanAdded,
  addTotal,
  disabled,
  onAddPlan,
  onKeepStandard,
}: {
  open: boolean;
  onClose: () => void;
  productTitle: string;
  /** The plan, as offered for one device. */
  plan: WarrantyT;
  /** The included warranty's length, e.g. "12 Months". */
  standardWarranty: string;
  /** Every line of the device already has the plan. */
  isPlanAdded: boolean;
  /**
   * What adding charges: each uncovered line's plan, times that line's
   * quantity (it can be more than one device's plan).
   */
  addTotal: number;
  disabled: boolean;
  onAddPlan: () => Promise<void>;
  onKeepStandard: () => Promise<void>;
}) => {
  // Per-month price and duration only for plans sold in months (see plan.ts).
  const months = getPlanMonths(plan);
  const monthlyPrice = getPlanMonthlyPrice(plan);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-[70]">
      <div className="fixed inset-0 bg-gray-700/50" aria-hidden />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
          <Dialog.Panel className="relative w-full max-w-[640px] rounded-2xl bg-white p-4 text-[13px] text-gray-700 shadow-xl sm:p-6">
            <button
              type="button"
              onClick={onClose}
              aria-label={t`Close`}
              className="absolute right-3 top-2 flex h-9 w-9 items-center justify-center rounded-full border border-[#E6E7EB] hover:border-gray-700 sm:right-4 sm:top-4"
            >
              <X className="h-4 w-4" />
            </button>
            <Dialog.Title
              as="h2"
              className="pr-12 text-base font-medium leading-tight"
            >
              {t`Protect your ${{ product: productTitle }}`}
            </Dialog.Title>
            <p className="mt-0.5 leading-tight text-gray-700/60">
              {t`What's covered with ReebeloCare vs the included warranty.`}
            </p>

            <table className="mt-4 w-full border-collapse">
              <thead>
                <tr>
                  <td />
                  <th
                    scope="col"
                    className={classNames(
                      // The two plans take 30% each on phones (24% from `sm`), the labels the rest.
                      'w-[30%] rounded-t-lg px-2 py-2.5 text-center sm:w-[24%] sm:px-3',
                      PLAN_COLUMN,
                    )}
                  >
                    <span className="block text-[13px] font-medium sm:text-sm">
                      {t`ReebeloCare`}
                    </span>
                    {/* The plan's full price for one device, and its per-month
                        equivalent in brackets. */}
                    <span
                      className="block text-xs font-normal text-gray-700/60"
                      translate="no"
                    >
                      {priceFormatSuperscript(priceFormater.long(plan.price))}
                      {monthlyPrice != null && (
                        <>
                          {' ('}
                          {priceFormatSuperscript(
                            priceFormater.long(monthlyPrice),
                            t`/mo`,
                          )}
                          )
                        </>
                      )}
                    </span>
                  </th>
                  <th
                    scope="col"
                    className="w-[30%] px-2 py-2.5 text-center sm:w-[24%] sm:px-3"
                  >
                    <span className="block text-[13px] font-medium sm:text-sm">
                      {t`Warranty`}
                    </span>
                    <span className="block text-xs font-normal text-gray-700/60">
                      {t`Included, free`}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Always shown, for the included warranty's length; the
                    plan's own is only given in months (see plan.ts). */}
                <Row
                  label={t`Plan duration`}
                  plan={
                    <span className="font-medium">
                      {months != null
                        ? t`${{ count: String(months) }} Months`
                        : '—'}
                    </span>
                  }
                  standard={standardWarranty}
                />
                <Row
                  label={t`Defects & malfunctions`}
                  plan={<Covered covered />}
                  standard={<Covered covered />}
                />
                <Row
                  label={t`Accidental damage`}
                  hint={t`Drops, spills, cracks`}
                  plan={<Covered covered />}
                  standard={<Covered covered={false} />}
                />
                <Row
                  label={t`Replacement or store credit if unrepairable`}
                  plan={<Covered covered />}
                  standard={<Covered covered={false} />}
                />
              </tbody>
            </table>

            <p className="mt-4 text-xs text-gray-700/60">
              {t`Your warranty covers defects and malfunctions where no damage is observed. ReebeloCare covers physical and liquid damage from day one, and picks up on defects and malfunctions once your warranty expires.`}
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                disabled={disabled}
                onClick={async () => {
                  await onKeepStandard();
                  onClose();
                }}
                className="h-11 rounded-lg border border-gray-700 font-medium transition duration-150 hover:bg-gray-700/5 disabled:cursor-wait disabled:opacity-60"
              >
                {t`Keep included warranty`}
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={async () => {
                  if (!isPlanAdded) {
                    await onAddPlan();
                  }

                  onClose();
                }}
                className="h-11 rounded-lg bg-gray-700 font-medium text-white transition duration-150 hover:bg-gray-700/90 disabled:cursor-wait disabled:opacity-60"
              >
                {/* Already covered, the button just confirms the choice. */}
                {isPlanAdded ? (
                  t`Keep ReebeloCare`
                ) : (
                  <span translate="no">
                    {t`Add ReebeloCare,`}{' '}
                    {priceFormatSuperscript(priceFormater.long(addTotal))}
                  </span>
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default CompareCoverageModal;
